import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Box, Heading, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react';
import { Phone, Users, Calendar } from 'lucide-react';
import Card from '@/components/ui/Card';
import PageHeader from '@/components/ui/PageHeader';
import Button from '@/components/ui/Button';
import EntityTableSection from '@/components/section/EntityTableSection';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import { displayTime, errorMessage, memberName, useFrontlineSetupQuery, useFrontlineShiftsQuery, useUpdateFrontlineMutation, type FrontlineSetup } from '@/entities/frontline/api';
import SetupEditor, { type Editor } from './SetupEditor';
export default function FrontlineSetupPage({ organizationId: selectedId }: { organizationId?: string } = {}) {
  const {
    organizationId: routeId = ''
  } = useParams();
  const organizationId = selectedId ?? routeId;
  const query = useFrontlineSetupQuery(organizationId);
  if (query.isError) return <VStack align="start"><Text role="alert">Could not load Frontline setup.</Text><Button onClick={() => void query.refetch()}>Retry</Button></VStack>;
  if (!query.currentData) return <Text role="status">Loading setup…</Text>;
  return <Setup key={organizationId} setup={query.currentData} refresh={() => void query.refetch()} />;
}
function Setup({
  setup,
  refresh
}: {
  setup: FrontlineSetup;
  refresh: () => void;
}) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState<{
    title: string;
    message: string;
    path: string;
    method: 'DELETE' | 'POST';
    success: string;
  } | null>(null);
  const [mutate, {
    isLoading
  }] = useUpdateFrontlineMutation();
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const parsedDate = new Date(`${fromDate}T00:00:00Z`);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(fromDate) && Number.isFinite(parsedDate.getTime());
  const start = validDate ? new Date(`${fromDate}T00:00:00Z`) : new Date();
  const from = start.toISOString().replace('.000Z', '+00:00');
  const to = new Date(start.getTime() + 31 * 86400000).toISOString().replace('.000Z', '+00:00');
  const shifts = useFrontlineShiftsQuery({
    id: setup.id,
    from,
    to
  }, {
    skip: !validDate
  });
  const path = `organizations/${setup.id}`;
  const name = (id: string | null) => id ? memberName(setup.members.find(m => m.id === id)) : 'None';
  const change = async (args: Parameters<typeof mutate>[0], success: string) => {
    setError('');
    setNotice('');
    try {
      await mutate(args).unwrap();
      setNotice(success);
      setPending(null);
      return true;
    } catch (e) {
      setPending(null);
      setError(errorMessage(e));
      return false;
    }
  };
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice('Copied.');
    } catch {
      setError('Could not copy. Select the text and copy it manually.');
    }
  };
  return <VStack align="stretch" gap={6}>
    <PageHeader title={setup.name} subtitle={`Frontline setup · ${setup.timezone}`} onRefresh={refresh} />
    {error && <Text role="alert" color="red.600">{error}</Text>}{notice && <Text role="status" color="green.600">{notice}</Text>}
    <Card><HStack justify="space-between" flexWrap="wrap" gap={4}><Box><Heading size="md">Dashboard inbox</Heading><Text mt={2} color="gray.500">Show Frontline to active members of this organization.</Text><Text mt={1} fontSize="sm" color="gray.500">Hiding the inbox does not stop Retell calls or notifications. Manage number mappings separately.</Text></Box><HStack><Badge colorPalette={setup.enabled ? 'green' : 'gray'}>{setup.enabled ? 'Enabled' : 'Disabled'}</Badge><Button loading={isLoading} variant="outline" onClick={() => void change({
            path,
            method: 'PATCH',
            body: {
              enabled: !setup.enabled
            }
          }, 'Inbox setting saved.')}>{setup.enabled ? 'Disable inbox' : 'Enable inbox'}</Button></HStack></HStack></Card>
    <section><PageHeader title="Phone numbers" subtitle="Map each Retell inbound number to this practice." onAddNew={() => setEditor({
        kind: 'phone'
      })} addButtonText="Add number" mb={4} />
      <EntityTableSection data={setup.phoneNumbers} keyExtractor={p => p.id} columns={[{
        key: 'phoneNumber',
        title: 'Phone number',
        variant: 'primary'
      }, {
        key: 'label',
        title: 'Label'
      }, {
        key: 'retellAgentId',
        title: 'Retell agent'
      }, {
        key: 'isActive',
        title: 'Status',
        render: p => <Badge colorPalette={p.isActive ? 'green' : 'gray'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
      }, {
        key: 'actions',
        title: '',
        render: p => <HStack><Button size="sm" variant="outline" onClick={() => setEditor({
            kind: 'phone',
            value: p
          })}>Edit</Button><Button size="sm" variant="ghost" disabled={isLoading} onClick={() => setPending({
            title: 'Remove phone mapping?',
            message: `Incoming calls to ${p.phoneNumber} will no longer map to this organization. Existing call history is retained.`,
            path: `phone-numbers/${p.id}`,
            method: 'DELETE',
            success: 'Phone mapping removed.'
          })}>Remove</Button></HStack>
      }]} emptyState={{
        icon: Phone,
        title: 'No numbers mapped',
        description: 'Add the phone number used by this organization in Retell.'
      }} />
    </section>
    <section><PageHeader title="Escalation contacts" subtitle="Organization members who can receive urgent-call notifications." onAddNew={() => setEditor({
        kind: 'contact'
      })} addButtonText="Add contact" mb={4} />
      <EntityTableSection data={setup.contacts} keyExtractor={c => c.userId} columns={[{
        key: 'userId',
        title: 'Member',
        render: c => name(c.userId)
      }, {
        key: 'phoneNumber',
        title: 'Phone'
      }, {
        key: 'acceptsEscalations',
        title: 'Notifications',
        render: c => <Badge colorPalette={c.acceptsEscalations ? 'green' : 'gray'}>{c.acceptsEscalations ? 'Accepting' : 'Disabled'}</Badge>
      }, {
        key: 'lastTestSentAt',
        title: 'Last test sent',
        render: c => c.lastTestSentAt ? displayTime(c.lastTestSentAt, setup.timezone) : 'Not tested'
      }, {
        key: 'actions',
        title: '',
        render: c => <HStack flexWrap="wrap"><Button size="sm" variant="outline" onClick={() => setEditor({
            kind: 'contact',
            value: c
          })}>Edit</Button><Button size="sm" variant="outline" disabled={isLoading} onClick={() => setPending({
            title: 'Send a test SMS?',
            message: `Send a Wellprept test message to ${name(c.userId)} at ${c.phoneNumber}?`,
            path: `${path}/contacts/${c.userId}/test`,
            method: 'POST',
            success: 'Test SMS accepted by the messaging service. This does not confirm handset delivery.'
          })}>Test SMS</Button><Button size="sm" variant="ghost" disabled={isLoading} onClick={() => setPending({
            title: 'Remove escalation contact?',
            message: `Remove ${name(c.userId)} as an escalation contact? Contacts used by current or future shifts cannot be removed.`,
            path: `${path}/contacts/${c.userId}`,
            method: 'DELETE',
            success: 'Contact removed.'
          })}>Remove</Button></HStack>
      }]} emptyState={{
        icon: Users,
        title: 'No escalation contacts',
        description: 'Add phone numbers for your primary and backup doctors.'
      }} />
    </section>
    <section><PageHeader title="On-call shifts" subtitle={`Schedule shown in ${setup.timezone}. A primary doctor is required; backup is optional.`} onAddNew={() => setEditor({
        kind: 'shift'
      })} addButtonText="Add shift" mb={4} />
      <HStack mb={4} flexWrap="wrap"><label htmlFor="shift-from">Show 31 days from</label><Input id="shift-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} maxW="220px" /><Text fontSize="sm" color="gray.500">Date range boundaries use UTC.</Text></HStack>
      {shifts.isError && <Text role="alert" color="red.600">Unable to load shifts. <Button variant="ghost" onClick={() => void shifts.refetch()}>Retry</Button></Text>}
      <EntityTableSection data={shifts.currentData?.data ?? []} isLoading={shifts.isFetching} keyExtractor={s => s.id} columns={[{
        key: 'primaryUserId',
        title: 'Primary',
        render: s => name(s.primaryUserId)
      }, {
        key: 'backupUserId',
        title: 'Backup',
        render: s => name(s.backupUserId)
      }, {
        key: 'startsAt',
        title: 'Starts',
        render: s => displayTime(s.startsAt, setup.timezone)
      }, {
        key: 'endsAt',
        title: 'Ends',
        render: s => displayTime(s.endsAt, setup.timezone)
      }, {
        key: 'actions',
        title: '',
        render: s => <HStack><Button size="sm" variant="outline" disabled={new Date(s.endsAt) <= new Date()} onClick={() => setEditor({
            kind: 'shift',
            value: s
          })}>Edit</Button><Button size="sm" variant="ghost" disabled={isLoading || new Date(s.endsAt) <= new Date()} onClick={() => setPending({
            title: 'Remove on-call shift?',
            message: 'This removes the scheduled coverage. Add replacement coverage if needed.',
            path: `${path}/shifts/${s.id}`,
            method: 'DELETE',
            success: 'Shift removed.'
          })}>Remove</Button></HStack>
      }]} emptyState={{
        icon: Calendar,
        title: 'No shifts in this range',
        description: 'Add primary and backup coverage or select another date range.'
      }} />
    </section>
    <Card><Box as="details"><Box as="summary" cursor="pointer" fontWeight="semibold">Advanced settings</Box><Box mt={4}><Heading size="md" mb={3}>Retell integration details</Heading><Text mb={4}>This webhook URL is shared by all organizations in this environment. The inbound phone mapping identifies the organization. Assign the practice number to its agent in Retell and use the analysis fields below.</Text><HStack mb={5}><Input aria-label="Retell webhook URL" readOnly value={setup.webhookUrl} /><Button variant="outline" onClick={() => void copy(setup.webhookUrl)}>Copy URL</Button></HStack>
      <Box as="details"><Box as="summary" cursor="pointer" fontWeight="medium">Inbound analysis schema</Box><Textarea mt={3} readOnly aria-label="Inbound analysis schema" rows={8} value={JSON.stringify(setup.analysisSchema, null, 2)} /><Button mt={2} variant="outline" onClick={() => void copy(JSON.stringify(setup.analysisSchema, null, 2))}>Copy inbound schema</Button></Box>
      <Box as="details" mt={4}><Box as="summary" cursor="pointer" fontWeight="medium">Notifier analysis schema</Box><Textarea mt={3} readOnly aria-label="Notifier analysis schema" rows={5} value={JSON.stringify(setup.notifierAnalysisSchema, null, 2)} /><Button mt={2} variant="outline" onClick={() => void copy(JSON.stringify(setup.notifierAnalysisSchema, null, 2))}>Copy notifier schema</Button></Box>
      <Text mt={5} fontSize="sm" color="gray.500">Automatic escalation activation: {setup.escalationActivationConfigured ? 'Configured at server level' : 'Not configured'}. Contacts and shifts alone do not activate notifications. Verify the SMS confirmation page and deployment configuration before activating escalation.</Text>
    </Box></Box></Card>
    {editor && <SetupEditor setup={setup} editor={editor} onClose={() => setEditor(null)} />}
    <ConfirmationModal isOpen={pending !== null} onClose={() => {
      if (!isLoading) setPending(null);
    }} onConfirm={() => {
      if (pending) void change({
        path: pending.path,
        method: pending.method
      }, pending.success);
    }} title={pending?.title} message={pending?.message} confirmText={pending?.method === 'POST' ? 'Send test SMS' : 'Remove'} type={pending?.method === 'POST' ? 'info' : 'danger'} isLoading={isLoading} />
  </VStack>;
}
